/**
 * All UI styling, injected once.
 *
 * A single stylesheet rather than a CSS framework, so the packaged build has no
 * extra dependency and no external font or asset fetches — which also keeps
 * docs/ASSET_LICENSES.md at zero external assets.
 *
 * The V1 HUD gave money, time, day, net worth and reputation identical visual
 * weight, so nothing read first (VISUAL_AUDIT V-06). This one has a deliberate
 * hierarchy: money is the largest thing on screen after the world, the current
 * objective sits directly under it, and everything else is either small or
 * lives on the phone.
 */
export const UI_CSS = `
:root {
  --bg: #0d1118;
  --panel: rgba(13, 17, 24, 0.86);
  --panel-strong: rgba(13, 17, 24, 0.95);
  --panel-solid: #141922;
  --raise: rgba(255, 255, 255, 0.05);
  --line: rgba(255, 255, 255, 0.10);
  --line-strong: rgba(255, 255, 255, 0.2);
  --text: #f0f4f8;
  --muted: #8f9bab;
  --dim: #6b7686;
  --accent: #ffd23f;
  --accent-deep: #e0a800;
  --good: #4fd48a;
  --bad: #ff6b5c;
  --warn: #f2b134;
  --info: #6fb4ff;
  --font: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  --shadow: 0 18px 44px rgba(0, 0, 0, 0.5);
  --r: 14px;
}
* { box-sizing: border-box; }
html, body {
  margin: 0; padding: 0; height: 100%; overflow: hidden; background: var(--bg);
  /* iOS Safari specifics. overscroll-behavior stops the whole page rubber-
     banding when a drag reaches the top of the screen; touch-action none
     stops the browser claiming drags as scrolls and swallowing double taps as
     zooms. Without these a camera drag near the top edge scrolls the page
     instead of turning the view. */
  overscroll-behavior: none; touch-action: none;
  -webkit-text-size-adjust: 100%;
}
body { font-family: var(--font); color: var(--text); -webkit-font-smoothing: antialiased; }
#app { position: fixed; inset: 0; }
#game-canvas { display: block; position: absolute; inset: 0; }

/*
 * A vignette over the rendered world, under the HUD.
 *
 * VISUAL_AUDIT V-25 asked for screen-space effects. A post-processing pass in
 * WebGL would cost a full-screen render target for this; one CSS gradient
 * costs nothing, darkens the frame edges the way a lens does, and pulls the
 * eye to the middle of the screen where the player is. It sits below the HUD,
 * so panels and text keep their full contrast.
 */
.le-vignette {
  position: absolute; inset: 0; pointer-events: none; z-index: 0;
  background: radial-gradient(ellipse 118% 96% at 50% 46%,
    transparent 42%, rgba(8,11,17,.16) 72%, rgba(8,11,17,.42) 100%);
}
.le-ui { position: absolute; inset: 0; pointer-events: none; font-family: var(--font); z-index: 1; }
.le-ui button, .le-ui input { font-family: inherit; }
.le-ui ::selection { background: rgba(255,210,63,.3); }

/* ================= money + clock: the primary read ================= */
.le-status {
  position: absolute; top: 18px; left: 50%; transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  pointer-events: auto;
}
.le-money {
  display: flex; align-items: baseline; gap: 12px;
  background: var(--panel); border: 1px solid var(--line); border-radius: var(--r);
  padding: 9px 20px 11px; backdrop-filter: blur(10px); box-shadow: var(--shadow);
}
.le-money .amount {
  font-size: 30px; font-weight: 700; letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums; color: var(--good); line-height: 1;
  transition: color .2s ease;
}
.le-money .amount.negative { color: var(--bad); }
.le-money .amount.flash { animation: le-pop .5s ease; }
@keyframes le-pop {
  0% { transform: scale(1); }
  35% { transform: scale(1.13); text-shadow: 0 0 22px currentColor; }
  100% { transform: scale(1); }
}
.le-money .sep { width: 1px; height: 26px; background: var(--line); align-self: center; }
.le-money .clock {
  display: flex; flex-direction: column; align-items: flex-start; line-height: 1.25;
}
.le-money .clock .time {
  font-size: 17px; font-weight: 620; font-variant-numeric: tabular-nums;
}
.le-money .clock .date {
  font-size: 10.5px; letter-spacing: .09em; text-transform: uppercase; color: var(--muted);
}

.le-speed { display: flex; gap: 4px; }
.le-speed button {
  background: var(--panel); border: 1px solid var(--line); color: var(--muted);
  border-radius: 9px; min-width: 34px; height: 27px; cursor: pointer;
  font-size: 11.5px; font-weight: 650; backdrop-filter: blur(10px);
  transition: all .15s ease;
}
.le-speed button:hover { color: var(--text); border-color: var(--line-strong); }
.le-speed button.on {
  background: var(--accent); color: #10141c; border-color: var(--accent);
}

/* ================= objective ================= */
.le-objective {
  position: absolute; top: 18px; left: 18px; width: 268px; pointer-events: auto;
  background: var(--panel); border: 1px solid var(--line); border-left: 3px solid var(--accent);
  border-radius: var(--r); padding: 12px 15px; backdrop-filter: blur(10px);
  box-shadow: var(--shadow);
}
.le-objective .eyebrow {
  font-size: 9.5px; letter-spacing: .13em; text-transform: uppercase;
  color: var(--accent); font-weight: 700; margin-bottom: 5px;
}
.le-objective .goal { font-size: 14.5px; font-weight: 620; line-height: 1.35; }
.le-objective .hint { font-size: 12px; color: var(--muted); margin-top: 6px; line-height: 1.45; }
.le-objective .track {
  height: 4px; background: rgba(255,255,255,.08); border-radius: 2px;
  overflow: hidden; margin-top: 10px;
}
.le-objective .track i {
  display: block; height: 100%; border-radius: 2px;
  background: linear-gradient(90deg, var(--info), var(--accent));
  transition: width .5s cubic-bezier(.2,.8,.2,1);
}
.le-objective .track-label {
  display: flex; justify-content: space-between; font-size: 10.5px;
  color: var(--dim); margin-top: 5px; font-variant-numeric: tabular-nums;
}

/* ================= condition (small, secondary) ================= */
.le-vitals {
  position: absolute; top: 18px; right: 18px; display: flex; gap: 7px;
  pointer-events: auto;
}
.le-vital {
  background: var(--panel); border: 1px solid var(--line); border-radius: 11px;
  padding: 7px 10px; backdrop-filter: blur(10px); min-width: 62px;
}
.le-vital .k {
  font-size: 9px; letter-spacing: .1em; text-transform: uppercase; color: var(--dim);
  display: flex; justify-content: space-between; align-items: center; gap: 6px;
}
.le-vital .k b { font-size: 11px; color: var(--muted); font-variant-numeric: tabular-nums; }
.le-vital .bar { height: 3px; background: rgba(255,255,255,.09); border-radius: 2px; margin-top: 5px; }
.le-vital .bar i { display: block; height: 100%; border-radius: 2px; transition: width .4s ease, background .3s ease; }
.le-vital.low { border-color: rgba(255,107,92,.5); }

/* ================= interaction prompt ================= */
.le-prompt {
  position: absolute; left: 50%; bottom: 132px; transform: translateX(-50%) translateY(8px);
  background: var(--panel-strong); border: 1px solid var(--line-strong); border-radius: 12px;
  padding: 11px 18px; display: flex; align-items: center; gap: 11px;
  backdrop-filter: blur(10px); box-shadow: var(--shadow);
  opacity: 0; pointer-events: none; transition: opacity .16s ease, transform .16s ease;
}
.le-prompt.show { opacity: 1; transform: translateX(-50%) translateY(0); }
.le-prompt .key {
  background: var(--accent); color: #10141c; border-radius: 6px; padding: 3px 9px;
  font-weight: 750; font-size: 12.5px; box-shadow: 0 2px 0 var(--accent-deep);
}
.le-prompt .label { font-size: 15px; font-weight: 600; }
.le-prompt .where { color: var(--muted); font-size: 12.5px; }

/* ================= work in progress ================= */
.le-work {
  position: absolute; left: 50%; bottom: 62px; transform: translateX(-50%);
  width: 420px; background: var(--panel-strong); border: 1px solid var(--line);
  border-radius: var(--r); padding: 13px 17px; display: none; backdrop-filter: blur(10px);
  box-shadow: var(--shadow);
}
.le-work.show { display: block; }
.le-work .row { display: flex; justify-content: space-between; align-items: baseline; font-size: 13.5px; margin-bottom: 9px; }
.le-work .row .title { font-weight: 620; }
.le-work .row .t { color: var(--muted); font-variant-numeric: tabular-nums; font-size: 12.5px; }
.le-work .bar { height: 10px; background: rgba(255,255,255,.09); border-radius: 5px; overflow: hidden; }
.le-work .bar i {
  display: block; height: 100%; border-radius: 5px;
  background: linear-gradient(90deg, var(--good), var(--accent));
  transition: width .18s linear;
}
.le-work .row.mow { margin-top: 10px; margin-bottom: 6px; font-size: 12.5px; }
.le-work .row.mow .title { color: var(--muted); font-weight: 560; }
.le-work .bar.mow { height: 8px; }
.le-work .bar.mow i { background: linear-gradient(90deg, #6fae4a, #a8e06a); }
.le-work .bar.mow.low i { background: linear-gradient(90deg, #b0722c, #d8a03a); }
.le-work .hint { font-size: 11.5px; color: var(--dim); margin-top: 9px; }
/* Equipment condition, in the tool shop. */
.le-item .row.cond {
  display: flex; justify-content: space-between; align-items: baseline;
  margin-top: 10px; margin-bottom: 5px; font-size: 12px;
}
.le-item .row.cond .title { color: var(--muted); font-weight: 560; }
.le-item .row.cond .t { color: var(--muted); font-variant-numeric: tabular-nums; }
.le-item .bar.cond { height: 7px; background: rgba(255,255,255,.09); border-radius: 4px; overflow: hidden; }
.le-item .bar.cond i { display: block; height: 100%; background: linear-gradient(90deg, #4f8fd4, #7fb8f0); }
.le-item .bar.cond.low i { background: linear-gradient(90deg, #b0722c, #d8a03a); }
/* The per-task breakdown. Only rendered when a job has more than one part, so
   a player with just a mower sees the panel exactly as it was. */
.le-tasks { display: flex; gap: 6px; margin-top: 7px; flex-wrap: wrap; }
.le-tasks:empty { display: none; }
.le-tasks span {
  font-size: 10px; text-transform: uppercase; letter-spacing: .06em; font-weight: 650;
  background: rgba(255,255,255,.07); color: var(--muted);
  border-radius: 5px; padding: 2px 7px;
}
.le-tasks span.done { background: rgba(79,212,138,.18); color: var(--good); }

/* ================= toasts ================= */
.le-toasts {
  position: absolute; right: 18px; top: 92px; width: 292px;
  display: flex; flex-direction: column; gap: 8px; align-items: flex-end;
}
.le-toast {
  background: var(--panel-strong); border: 1px solid var(--line);
  border-left: 3px solid var(--muted);
  border-radius: 11px; padding: 10px 13px; font-size: 13px; width: 100%;
  backdrop-filter: blur(10px); box-shadow: var(--shadow);
  animation: le-slide .28s cubic-bezier(.2,.9,.3,1);
  display: flex; justify-content: space-between; align-items: baseline; gap: 10px;
}
.le-toast.good { border-left-color: var(--good); }
.le-toast.bad { border-left-color: var(--bad); }
.le-toast.warn { border-left-color: var(--warn); }
.le-toast .title { flex: 1; line-height: 1.35; }
.le-toast .amount { font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
.le-toast.good .amount { color: var(--good); }
.le-toast.bad .amount { color: var(--bad); }
.le-toast.warn .amount { color: var(--warn); }
.le-toast.leaving { animation: le-slide-out .25s ease forwards; }
@keyframes le-slide { from { opacity: 0; transform: translateX(24px) scale(.97); } to { opacity: 1; transform: none; } }
@keyframes le-slide-out { to { opacity: 0; transform: translateX(24px); height: 0; padding-block: 0; margin-top: -8px; } }

/* ================= milestone celebration ================= */
.le-celebrate {
  position: absolute; left: 50%; top: 24%; transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  pointer-events: none; opacity: 0;
  /* The banner lands over whatever the player happens to be standing in front
     of. Without a scrim the sub-line disappears into a pale pavement. */
  padding: 30px 90px 34px;
  background: radial-gradient(58% 100% at 50% 50%, rgba(6,9,14,.86) 0%, rgba(6,9,14,.62) 32%, rgba(6,9,14,.26) 62%, transparent 86%);
}
.le-celebrate.show { animation: le-celebrate 2.8s cubic-bezier(.2,.8,.2,1) forwards; }
.le-celebrate .eyebrow {
  font-size: 11px; letter-spacing: .28em; text-transform: uppercase;
  color: var(--accent); font-weight: 700;
}
.le-celebrate .headline {
  font-size: 34px; font-weight: 750; letter-spacing: -0.02em; text-align: center;
  text-shadow: 0 4px 30px rgba(0,0,0,.75), 0 0 40px rgba(255,210,63,.35);
}
.le-celebrate .sub { font-size: 14.5px; color: #dfe5ee; text-shadow: 0 2px 14px rgba(0,0,0,.9); }
.le-celebrate .rule {
  width: 130px; height: 2px; margin-top: 4px;
  background: linear-gradient(90deg, transparent, var(--accent), transparent);
}
@keyframes le-celebrate {
  0% { opacity: 0; transform: translateX(-50%) translateY(14px) scale(.94); }
  12% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
  78% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
  100% { opacity: 0; transform: translateX(-50%) translateY(-16px) scale(.99); }
}

/* ================= phone ================= */
.le-phonebtn {
  position: absolute; right: 20px; bottom: 20px; pointer-events: auto;
  background: var(--panel-strong); border: 1px solid var(--line); color: var(--text);
  border-radius: 13px; padding: 11px 17px; cursor: pointer; font-size: 13.5px; font-weight: 620;
  display: flex; align-items: center; gap: 9px; backdrop-filter: blur(10px);
  box-shadow: var(--shadow); transition: all .16s ease;
}
.le-phonebtn:hover { border-color: var(--line-strong); transform: translateY(-1px); }
.le-phonebtn .glyph { font-size: 15px; }
.le-phonebtn .badge {
  background: var(--accent); color: #10141c; border-radius: 10px; padding: 1px 7px;
  font-size: 11px; font-weight: 750; min-width: 20px; text-align: center;
}

.le-modal {
  position: absolute; inset: 0; background: rgba(5, 8, 13, 0.55);
  display: none; align-items: center; justify-content: center; pointer-events: auto;
  backdrop-filter: blur(4px); animation: le-fade .18s ease;
}
.le-modal.show { display: flex; }
@keyframes le-fade { from { opacity: 0; } to { opacity: 1; } }

.le-phone {
  width: 432px; max-width: calc(100vw - 32px); height: 664px; max-height: calc(100vh - 44px);
  background: var(--panel-solid); border: 1px solid var(--line-strong); border-radius: 26px;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 34px 90px rgba(0,0,0,.65);
  animation: le-rise .24s cubic-bezier(.2,.9,.3,1);
}
@keyframes le-rise { from { opacity: 0; transform: translateY(18px) scale(.985); } to { opacity: 1; transform: none; } }

.le-phone-head {
  padding: 16px 20px 12px; border-bottom: 1px solid var(--line);
  display: flex; justify-content: space-between; align-items: center;
  background: linear-gradient(180deg, rgba(255,255,255,.035), transparent);
}
.le-phone-head .title { font-size: 16px; font-weight: 650; letter-spacing: -0.01em; }
.le-phone-head .sub { font-size: 11.5px; color: var(--muted); margin-top: 3px; }
.le-phone-head button {
  background: transparent; border: 1px solid var(--line); color: var(--muted);
  border-radius: 9px; width: 30px; height: 30px; cursor: pointer; font-size: 16px; line-height: 1;
  transition: all .15s ease;
}
.le-phone-head button:hover { color: var(--text); border-color: var(--line-strong); }

.le-tabs {
  display: flex; gap: 2px; padding: 9px 11px; border-bottom: 1px solid var(--line);
}
.le-tabs button {
  flex: 1 1 0; min-width: 0; background: transparent; border: none; color: var(--dim);
  padding: 8px 2px 7px; border-radius: 9px; cursor: pointer; font-size: 10px; font-weight: 620;
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  transition: all .15s ease; position: relative;
}
.le-tabs button .glyph { font-size: 15px; line-height: 1; filter: grayscale(1) opacity(.72); transition: filter .15s ease; }
.le-tabs button:hover { color: var(--text); background: var(--raise); }
.le-tabs button.on { background: rgba(255,210,63,.13); color: var(--accent); }
.le-tabs button.on .glyph { filter: none; }
.le-tabs button .dot {
  position: absolute; top: 5px; right: 50%; margin-right: -16px;
  width: 6px; height: 6px; border-radius: 3px; background: var(--accent);
}

.le-phone-body { flex: 1; overflow-y: auto; padding: 14px 16px 20px; scrollbar-width: thin; }
.le-phone-body::-webkit-scrollbar { width: 8px; }
.le-phone-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,.13); border-radius: 4px; }

.le-item {
  background: var(--raise); border: 1px solid var(--line);
  border-radius: 12px; padding: 12px 14px; margin-bottom: 9px;
  transition: border-color .15s ease, transform .15s ease;
}
.le-item:hover { border-color: var(--line-strong); }
.le-item.accent { border-color: rgba(255,210,63,.34); background: rgba(255,210,63,.05); }
.le-item.best { border-color: rgba(79,212,138,.4); background: rgba(79,212,138,.05); }
.le-item .head { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; }
.le-item .name { font-size: 14px; font-weight: 630; }
.le-item .pay { font-size: 15.5px; font-weight: 720; color: var(--good); font-variant-numeric: tabular-nums; }
.le-item .meta { font-size: 12px; color: var(--muted); margin-top: 7px; line-height: 1.55; }
.le-item .facts { display: flex; gap: 12px; margin-top: 8px; flex-wrap: wrap; }
.le-item .fact { display: flex; flex-direction: column; gap: 1px; }
.le-item .fact .fk { font-size: 9px; letter-spacing: .09em; text-transform: uppercase; color: var(--dim); }
.le-item .fact .fv { font-size: 12.5px; font-weight: 620; font-variant-numeric: tabular-nums; }
.le-item .fact .fv.hi { color: var(--accent); }
.le-item .tag {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 9.5px; text-transform: uppercase; letter-spacing: .07em; font-weight: 650;
  background: rgba(255,255,255,.08); border-radius: 5px; padding: 2px 7px; margin-right: 5px;
}
.le-item .tag.contract { background: rgba(79,212,138,.18); color: var(--good); }
.le-item .tag.accepted { background: rgba(255,210,63,.18); color: var(--accent); }
.le-item .tag.crew { background: rgba(111,180,255,.18); color: var(--info); }
.le-item .tag.best { background: rgba(79,212,138,.2); color: var(--good); }

.le-actions { display: flex; gap: 7px; margin-top: 11px; }
.le-actions button {
  flex: 1; background: var(--raise); border: 1px solid var(--line); color: var(--text);
  border-radius: 9px; padding: 8px; cursor: pointer; font-size: 12.5px; font-weight: 620;
  transition: all .14s ease;
}
.le-actions button:hover:not(:disabled) { background: rgba(255,255,255,.12); border-color: var(--line-strong); }
.le-actions button:active:not(:disabled) { transform: translateY(1px); }
.le-actions button.primary {
  background: var(--accent); color: #10141c; border-color: var(--accent);
  box-shadow: 0 2px 0 var(--accent-deep);
}
.le-actions button.primary:hover:not(:disabled) { filter: brightness(1.08); }
.le-actions button.primary:active:not(:disabled) { box-shadow: none; transform: translateY(2px); }
.le-actions button:disabled { opacity: .35; cursor: not-allowed; }

.le-empty {
  text-align: center; color: var(--dim); font-size: 13px; padding: 46px 24px; line-height: 1.65;
  white-space: pre-line;
}
.le-empty .glyph { font-size: 30px; display: block; margin-bottom: 12px; opacity: .5; }

.le-stat {
  display: flex; justify-content: space-between; gap: 12px; padding: 8px 0; font-size: 13px;
  border-bottom: 1px solid rgba(255,255,255,.05);
}
.le-stat:last-child { border-bottom: none; }
.le-stat .k { color: var(--muted); }
.le-stat .v { font-weight: 620; font-variant-numeric: tabular-nums; text-align: right; }
.le-stat .v.good { color: var(--good); }
.le-stat .v.bad { color: var(--bad); }
.le-stat .v.dim { color: var(--dim); font-weight: 500; }

.le-section {
  font-size: 9.5px; text-transform: uppercase; letter-spacing: .13em; color: var(--dim);
  margin: 20px 0 9px; font-weight: 700; display: flex; align-items: center; gap: 8px;
}
.le-section:first-child { margin-top: 0; }
.le-section::after { content: ''; flex: 1; height: 1px; background: var(--line); }
.le-note { font-size: 12px; color: var(--muted); line-height: 1.6; margin: 6px 0 10px; }
.le-note.warn { color: var(--warn); }

.le-input {
  width: 100%; background: rgba(0,0,0,.32); border: 1px solid var(--line);
  color: var(--text); border-radius: 9px; padding: 9px 11px; font-size: 13px; margin-bottom: 9px;
  outline: none; transition: border-color .15s ease;
}
.le-input:focus { border-color: var(--accent); }

.le-bigstat {
  display: flex; align-items: baseline; gap: 10px; margin-bottom: 4px;
}
.le-bigstat .n { font-size: 28px; font-weight: 720; font-variant-numeric: tabular-nums; }
.le-bigstat .n.good { color: var(--good); }
.le-bigstat .n.bad { color: var(--bad); }
.le-bigstat .u { font-size: 12px; color: var(--muted); }

/* ================= day summary ================= */
.le-summary {
  width: 448px; max-width: calc(100vw - 32px); background: var(--panel-solid);
  border: 1px solid var(--line-strong); border-radius: 20px; padding: 26px 28px 24px;
  box-shadow: 0 34px 90px rgba(0,0,0,.65);
  animation: le-rise .28s cubic-bezier(.2,.9,.3,1);
}
.le-summary h2 { margin: 0 0 4px; font-size: 22px; letter-spacing: -0.02em; }
.le-summary .sub { color: var(--muted); font-size: 12.5px; margin-bottom: 18px; }
.le-summary .net {
  font-size: 34px; font-weight: 740; margin: 16px 0 14px; font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
}
.le-summary .net.good { color: var(--good); }
.le-summary .net.bad { color: var(--bad); }
.le-summary button {
  width: 100%; margin-top: 20px; background: var(--accent); color: #10141c; border: none;
  border-radius: 11px; padding: 12px; font-size: 14.5px; font-weight: 700; cursor: pointer;
  box-shadow: 0 3px 0 var(--accent-deep); transition: filter .14s ease;
}
.le-summary button:hover { filter: brightness(1.08); }
.le-summary button:active { box-shadow: none; transform: translateY(3px); }

/* ================= controls hint ================= */
.le-hint {
  position: absolute; left: 18px; bottom: 18px; font-size: 11.5px; color: var(--dim);
  background: var(--panel); border: 1px solid var(--line); border-radius: 11px;
  padding: 9px 13px; line-height: 1.75; backdrop-filter: blur(10px); pointer-events: auto;
  transition: opacity .3s ease;
}
.le-hint b { color: var(--muted); font-weight: 650; }
.le-hint.faded { opacity: .35; }


/* ================= title screen ================= */
.le-title {
  position: absolute; inset: 0; pointer-events: auto; display: flex;
  align-items: center; justify-content: flex-start;
  opacity: 1; transition: opacity .5s ease;
}
.le-title.hidden { opacity: 0; pointer-events: none; }
.le-title-scrim {
  position: absolute; inset: 0;
  background:
    linear-gradient(100deg, rgba(5,8,13,.96) 0%, rgba(5,8,13,.90) 26%, rgba(5,8,13,.62) 46%, rgba(5,8,13,.16) 68%, transparent 88%),
    linear-gradient(180deg, rgba(5,8,13,.62) 0%, transparent 24%, transparent 66%, rgba(5,8,13,.82) 100%);
}
.le-title-inner {
  position: relative; padding: 0 0 0 clamp(38px, 8vw, 120px);
  display: flex; flex-direction: column; gap: 26px; max-width: 560px; width: 100%;
}

.le-brand { display: flex; flex-direction: column; gap: 6px; }
.le-brand-eyebrow {
  font-size: 11px; letter-spacing: .42em; text-transform: uppercase;
  color: var(--accent); font-weight: 650; padding-left: 3px;
}
.le-wordmark {
  display: flex; flex-direction: column; line-height: .88;
  filter: drop-shadow(0 8px 34px rgba(0,0,0,.7));
}
.le-wordmark .w1 {
  font-size: clamp(52px, 8.4vw, 96px); font-weight: 300; letter-spacing: .14em;
  color: var(--text);
}
.le-wordmark .w2 {
  font-size: clamp(52px, 8.4vw, 96px); font-weight: 800; letter-spacing: .015em;
  background: linear-gradient(100deg, #ffe488 0%, var(--accent) 42%, #d99a2b 100%);
  -webkit-background-clip: text; background-clip: text; color: transparent;
}
.le-brand-rule {
  width: 96px; height: 3px; margin-top: 12px; border-radius: 2px;
  background: linear-gradient(90deg, var(--accent), transparent);
}
.le-brand-tagline {
  font-size: 14.5px; color: #cfd6e2; letter-spacing: .04em; margin-top: 2px;
  text-shadow: 0 2px 12px rgba(0,0,0,.9);
}

.le-menu { display: flex; flex-direction: column; gap: 9px; max-width: 400px; }
.le-menu-btn {
  display: flex; flex-direction: column; align-items: flex-start; gap: 2px;
  background: rgba(13,17,24,.86); border: 1px solid var(--line);
  border-left: 3px solid transparent;
  color: var(--text); border-radius: 12px; padding: 12px 18px; cursor: pointer;
  text-align: left; backdrop-filter: blur(10px);
  transition: border-color .16s ease, background .16s ease, transform .16s ease;
}
.le-menu-btn .l { font-size: 16px; font-weight: 650; letter-spacing: -.01em; }
.le-menu-btn .s { font-size: 12px; color: #a9b3c2; }
/* Disabled entries must still be legible: a menu the player cannot read is a
   menu they assume is broken. .38 opacity over a bright sky was invisible. */
.le-menu-btn:disabled { opacity: .62; }
.le-menu-btn:hover:not(:disabled) {
  background: rgba(28,35,47,.86); border-left-color: var(--accent); transform: translateX(4px);
}
.le-menu-btn.primary {
  background: linear-gradient(100deg, rgba(255,210,63,.16), rgba(255,210,63,.05));
  border-color: rgba(255,210,63,.4); border-left-color: var(--accent);
}
.le-menu-btn.primary .l { color: var(--accent); }
.le-menu-btn.primary:hover:not(:disabled) { background: rgba(255,210,63,.22); }
.le-menu-btn:disabled { opacity: .38; cursor: not-allowed; }
.le-menu-btn.small {
  flex-direction: row; align-items: center; padding: 8px 14px; font-size: 12.5px;
  font-weight: 620; border-left-width: 1px; align-self: flex-start; margin-top: 4px;
}

.le-title-panel { max-width: 440px; }
.le-title-card {
  background: rgba(15,19,27,.9); border: 1px solid var(--line-strong);
  border-radius: 16px; padding: 18px 20px; backdrop-filter: blur(14px);
  box-shadow: var(--shadow); display: flex; flex-direction: column; gap: 12px;
}
.le-title-card-head {
  font-size: 10px; letter-spacing: .16em; text-transform: uppercase;
  color: var(--accent); font-weight: 700;
}
.le-save-row {
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  padding: 9px 0; border-bottom: 1px solid rgba(255,255,255,.06);
}
.le-save-row:last-of-type { border-bottom: none; }
.le-save-row .n { font-size: 13.5px; font-weight: 620; }
.le-save-row .d { font-size: 11.5px; color: var(--muted); margin-top: 2px; }
.le-save-row.empty .n, .le-save-row.empty .d { color: var(--dim); }

.le-setting { display: flex; flex-direction: column; gap: 7px; }
.le-setting-head {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 13px; gap: 12px;
}
.le-setting-head .v { color: var(--accent); font-variant-numeric: tabular-nums; font-size: 12.5px; }
.le-range {
  -webkit-appearance: none; appearance: none; width: 100%; height: 4px;
  background: rgba(255,255,255,.12); border-radius: 2px; outline: none;
}
.le-range::-webkit-slider-thumb {
  -webkit-appearance: none; width: 15px; height: 15px; border-radius: 50%;
  background: var(--accent); cursor: pointer; border: none;
}
.le-range::-moz-range-thumb {
  width: 15px; height: 15px; border-radius: 50%; background: var(--accent);
  cursor: pointer; border: none;
}
.le-choice { display: flex; gap: 5px; }
.le-choice button {
  flex: 1; background: var(--raise); border: 1px solid var(--line); color: var(--muted);
  border-radius: 8px; padding: 6px; font-size: 12px; cursor: pointer;
  text-transform: capitalize; transition: all .14s ease;
}
.le-choice button.on { background: var(--accent); color: #10141c; border-color: var(--accent); }
.le-toggle {
  background: var(--raise); border: 1px solid var(--line); color: var(--muted);
  border-radius: 8px; padding: 5px 14px; font-size: 12px; cursor: pointer;
}
.le-toggle.on { background: rgba(79,212,138,.2); border-color: rgba(79,212,138,.5); color: var(--good); }
.le-legend { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-top: 9px; }
.le-legend > span { white-space: nowrap; }
.le-title-note { font-size: 11.5px; color: var(--dim); line-height: 1.6; }

.le-title-footer {
  display: flex; gap: 14px; align-items: baseline; font-size: 11.5px;
  color: var(--muted); font-variant-numeric: tabular-nums;
}
.le-title-footer .dim { color: var(--dim); }

/* ================= pause menu ================= */
.le-pause {
  position: absolute; inset: 0; background: rgba(5,8,13,.62); backdrop-filter: blur(5px);
  display: none; align-items: center; justify-content: center; pointer-events: auto;
  z-index: 30; animation: le-fade .16s ease;
}
.le-pause.show { display: flex; }
.le-pause-card {
  width: 360px; max-width: calc(100vw - 32px);
  background: rgba(15,19,27,.94); border: 1px solid var(--line-strong);
  border-radius: 18px; padding: 22px 22px 18px; box-shadow: var(--shadow);
  display: flex; flex-direction: column; gap: 10px;
  animation: le-rise .2s cubic-bezier(.2,.9,.3,1);
}
.le-pause-card h2 {
  margin: 0 0 2px; font-size: 19px; letter-spacing: -.01em;
}
.le-pause-card .sub { font-size: 12px; color: var(--muted); margin-bottom: 8px; }

/* ================= boot + transitions ================= */
.le-fade {
  position: absolute; inset: 0; background: #06090e; pointer-events: none;
  opacity: 0; transition: opacity .45s ease; z-index: 40;
}
.le-fade.on { opacity: 1; }
.le-fade.block { pointer-events: auto; }

.le-boot {
  position: fixed; inset: 0; background: #06090e; z-index: 60;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 20px; transition: opacity .6s ease;
}
.le-boot.gone { opacity: 0; pointer-events: none; }
.le-boot .mark {
  font-size: 13px; letter-spacing: .5em; text-transform: uppercase;
  color: var(--accent); font-weight: 650;
  animation: le-breathe 2.4s ease-in-out infinite;
}
.le-boot .bar { width: 190px; height: 2px; background: rgba(255,255,255,.1); border-radius: 1px; overflow: hidden; }
.le-boot .bar i {
  display: block; height: 100%; width: 30%; border-radius: 1px; background: var(--accent);
  animation: le-sweep 1.15s cubic-bezier(.4,0,.2,1) infinite;
}
@keyframes le-breathe { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
@keyframes le-sweep {
  0% { transform: translateX(-110%); }
  100% { transform: translateX(360%); }
}

/* ================= debug ================= */
.le-debug {
  position: absolute; right: 18px; bottom: 78px; width: 256px; display: none;
  background: var(--panel-strong); border: 1px solid var(--warn); border-radius: 12px;
  padding: 12px 14px; pointer-events: auto; backdrop-filter: blur(10px);
}
.le-debug.show { display: block; }
.le-debug h3 {
  margin: 0 0 9px; font-size: 9.5px; letter-spacing: .13em; text-transform: uppercase;
  color: var(--warn); font-weight: 700;
}
.le-debug .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; }
.le-debug button {
  background: var(--raise); border: 1px solid var(--line); color: var(--text);
  border-radius: 7px; padding: 6px 4px; cursor: pointer; font-size: 11px;
}
.le-debug button:hover { background: rgba(255,255,255,.14); }
/* ================= drive prompt ================= */
.le-drive {
  position: absolute; left: 50%; bottom: 196px; transform: translateX(-50%) translateY(8px);
  display: none; align-items: center; gap: 10px; pointer-events: none;
  background: var(--panel); border: 1px solid var(--line); border-radius: 12px;
  padding: 8px 14px; backdrop-filter: blur(10px); box-shadow: var(--shadow);
  opacity: 0; transition: opacity .18s ease, transform .18s ease;
}
.le-drive.show { display: flex; opacity: 1; transform: translateX(-50%) translateY(0); }
.le-drive .key {
  background: var(--accent); color: #10141c; border-radius: 6px; padding: 1px 8px;
  font-size: 12px; font-weight: 750;
}
.le-drive .label { font-size: 14px; font-weight: 600; }
.le-drive .fuel {
  width: 54px; height: 4px; background: rgba(255,255,255,.12); border-radius: 2px;
  overflow: hidden;
}
.le-drive .fuel i { display: block; height: 100%; border-radius: 2px; transition: width .4s ease; }
/* Above the thumb cluster on a phone, and clear of the work panel. */
@media (max-width: 560px) { .le-drive { bottom: 250px; } }
@media (max-height: 460px) and (orientation: landscape) { .le-drive { bottom: 148px; } }

.le-item .tag.trait {
  background: rgba(111,180,255,.16); color: #9ccbff;
  border: 1px solid rgba(111,180,255,.28);
}

/* Relationship and district.
   Deliberately warmer than the blue trait tags: a trait is a property of the
   job, these two are properties of the world the player is building a place
   in, and the card is easier to scan when those read as different kinds of
   fact rather than one row of identical chips. */
.le-item .tag.loyal {
  background: rgba(255,210,63,.2); color: var(--accent);
  border: 1px solid rgba(255,210,63,.34);
}
.le-item .tag.regular {
  background: rgba(255,210,63,.12); color: #e8c964;
  border: 1px solid rgba(255,210,63,.22);
}
.le-item .tag.returning { background: rgba(255,255,255,.09); color: var(--muted); }
/* A district that is not calling yet. Deliberately quiet: it is a signpost,
   not an offer, and it must not compete with the jobs above it. */
.le-item.locked { opacity: .72; border-style: dashed; }
.le-item.locked .pay { font-size: 11px; color: var(--muted); font-weight: 650; }
.le-item .tag.wealthy {
  background: rgba(198,160,255,.16); color: #cdb0ff;
  border: 1px solid rgba(198,160,255,.3);
}
.le-item .tag.middle {
  background: rgba(140,200,180,.14); color: #9ed8c4;
  border: 1px solid rgba(140,200,180,.26);
}

/* ================= doorway prompt ================= */
/* Sits above the drive prompt, since both can be on screen at a driveway. */
.le-door {
  position: absolute; left: 50%; bottom: 244px; transform: translateX(-50%) translateY(8px);
  display: none; align-items: center; gap: 10px; pointer-events: none;
  background: var(--panel); border: 1px solid var(--line); border-radius: 12px;
  padding: 8px 14px; backdrop-filter: blur(10px); box-shadow: var(--shadow);
  opacity: 0; transition: opacity .18s ease, transform .18s ease;
}
.le-door.show { display: flex; opacity: 1; transform: translateX(-50%) translateY(0); }
.le-door .key {
  background: var(--accent); color: #10141c; border-radius: 6px; padding: 1px 8px;
  font-size: 12px; font-weight: 750;
}
.le-door .label { font-size: 14px; font-weight: 600; }
@media (max-width: 560px) { .le-door { bottom: 298px; font-size: 13px; } }
@media (max-height: 460px) and (orientation: landscape) { .le-door { bottom: 196px; } }

/* ================= touch controls ================= */
/*
 * Mounted only when the primary pointer is a finger, so nothing here affects
 * the desktop build. Everything is placed against the safe-area insets rather
 * than against the viewport edge: on a notched iPhone in landscape the sensor
 * housing eats one side and the home indicator eats the bottom, and a control
 * under either is a control the player cannot press.
 */
.le-touch-layer {
  position: absolute; inset: 0; z-index: 3; pointer-events: none;
  touch-action: none; -webkit-user-select: none; user-select: none;
  -webkit-touch-callout: none;
}
.le-touch-layer.hidden { display: none; }
/* Modal state: everything the thumbs use goes away and the phone gets the
   whole screen back. The menu button stays as the way out. */
.le-touch-layer.modal .le-touch-look,
.le-touch-layer.modal .le-touch-stick,
.le-touch-layer.modal .le-touch-buttons { display: none; }
.le-touch-look { position: absolute; inset: 0; pointer-events: auto; touch-action: none; }

.le-touch-stick {
  position: absolute; pointer-events: auto; touch-action: none;
  left: calc(env(safe-area-inset-left, 0px) + 26px);
  bottom: calc(env(safe-area-inset-bottom, 0px) + 30px);
  width: 148px; height: 148px; border-radius: 50%;
  background: rgba(10,14,21,.3); border: 1px solid rgba(255,255,255,.14);
  backdrop-filter: blur(5px);
  display: flex; align-items: center; justify-content: center;
  opacity: .55; transition: opacity .18s ease;
}
.le-touch-stick.active { opacity: .92; }
.le-touch-stick i {
  display: block; width: 62px; height: 62px; border-radius: 50%;
  background: rgba(255,255,255,.24); border: 1px solid rgba(255,255,255,.3);
  box-shadow: 0 4px 14px rgba(0,0,0,.4);
}

.le-touch-buttons {
  position: absolute; pointer-events: none;
  right: calc(env(safe-area-inset-right, 0px) + 22px);
  bottom: calc(env(safe-area-inset-bottom, 0px) + 30px);
  display: grid; grid-template-columns: repeat(2, auto); gap: 14px;
  align-items: end; justify-items: center;
}
.le-touch-btn {
  pointer-events: auto; touch-action: none; -webkit-tap-highlight-color: transparent;
  /* 64px is above the 44pt minimum Apple asks for and is what a thumb finds
     without looking, which is the only way anyone presses these. */
  width: 64px; height: 64px; border-radius: 50%;
  background: var(--panel-strong); border: 1px solid var(--line); color: var(--text);
  backdrop-filter: blur(10px); box-shadow: var(--shadow);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 1px; font-family: inherit; cursor: pointer;
  transition: transform .1s ease, background .12s ease, opacity .16s ease;
}
.le-touch-btn .g { font-size: 19px; font-weight: 700; line-height: 1; }
.le-touch-btn .l { font-size: 8.5px; letter-spacing: .09em; text-transform: uppercase; color: var(--dim); }
.le-touch-btn.down { transform: scale(.92); background: rgba(255,255,255,.16); }
.le-touch-btn.dim { opacity: .38; }
.le-touch-interact {
  /* Bottom-right of the cluster: the corner the thumb reaches without moving
     the hand, and the button pressed most often. */
  grid-column: 2; grid-row: 2; width: 78px; height: 78px;
  border-color: var(--accent); color: var(--accent);
}
.le-touch-interact .l { color: var(--accent); opacity: .8; }
.le-touch-run { grid-column: 2; grid-row: 1; }
.le-touch-run.latched { background: var(--accent); color: #10141c; border-color: var(--accent); }
.le-touch-run.latched .l { color: rgba(16,20,28,.72); }
.le-touch-phone { grid-column: 1; grid-row: 2; }
/* The cluster is 156px square and the stick is 148px: between them they own
   the bottom ~190px of a portrait phone, so nothing else may be placed there.
   The portrait rules below keep the HUD above that line. */
.le-touch-menu {
  position: absolute; width: 46px; height: 46px;
  top: calc(env(safe-area-inset-top, 0px) + 14px);
  left: calc(env(safe-area-inset-left, 0px) + 14px);
}
.le-touch-menu .l { display: none; }

/*
 * Chrome that the touch build replaces rather than keeps: the keyboard hint
 * strip names keys that do not exist on a phone, and the corner phone button
 * sits exactly where the thumb cluster goes.
 */
body.le-touch .le-hint { display: none; }
body.le-touch .le-phonebtn { display: none; }
/* The objective card would sit under the notch and over the menu button. */
body.le-touch .le-objective { top: calc(env(safe-area-inset-top, 0px) + 74px); }

/* ================= phone-sized screens ================= */
/*
 * One breakpoint at 560px covers every iPhone in portrait, and the landscape
 * rule below covers the same phones turned sideways, where height rather than
 * width is what runs out. Panels sized for a 1366px capture do not merely look
 * cramped at 390px - the phone modal was taller than the viewport, so its
 * action buttons were unreachable.
 */
@media (max-width: 560px) {
  /*
   * Portrait phones are laid out top-down rather than into the corners. The
   * desktop HUD puts money bottom-centre, vitals top-right and the objective
   * top-left, which on a 390pt screen collides three ways at once: the money
   * card lands under the joystick, the vitals sit behind the interact button,
   * and the speed buttons overlap both. Everything is stacked from the top
   * instead, and the bottom ~190pt is left entirely to the thumbs.
   */
  /* The money card measures about 344pt wide once the clock is told not to
     wrap, which is nearly the whole screen: it gets a row to itself, below the
     menu button and the vitals rather than beside them. */
  .le-status {
    top: calc(env(safe-area-inset-top, 0px) + 66px); bottom: auto; gap: 6px;
  }
  .le-money { padding: 5px 12px 6px; gap: 8px; border-radius: 11px; }
  .le-money .amount { font-size: 19px; }
  .le-money .sep { height: 20px; }
  /* The clock column wraps "MONDAY · DAY 1 · CLEAR" onto three lines and
     clips it at this width unless it is told not to. */
  .le-money .clock { white-space: nowrap; font-size: 10px; }
  .le-speed button { padding: 4px 8px; font-size: 10.5px; }

  .le-vitals {
    top: calc(env(safe-area-inset-top, 0px) + 12px); right: 8px; bottom: auto;
    flex-direction: row; gap: 4px;
  }
  .le-vital { min-width: 50px; padding: 4px 7px; }
  .le-vital .k { font-size: 8px; }
  .le-vital .k b { font-size: 10px; }

  /* Repeated for the touch selector: body.le-touch .le-objective outranks a
     bare class inside a media query, and would otherwise pull the card back
     up into the money row. */
  .le-objective,
  body.le-touch .le-objective {
    top: calc(env(safe-area-inset-top, 0px) + 196px);
    left: 8px; right: 8px; width: auto; padding: 9px 12px;
  }
  .le-objective .goal { font-size: 13px; }
  .le-objective .hint { font-size: 11px; margin-top: 4px; }

  .le-toasts {
    top: calc(env(safe-area-inset-top, 0px) + 306px);
    left: 8px; right: 8px; width: auto;
  }
  .le-prompt { bottom: 206px; font-size: 13px; }
  .le-work { left: 8px; right: 8px; bottom: 200px; transform: none; width: auto; }
  /* The panel grows upward from a fixed bottom, so every line it gains eats
     into the toasts above it. The task chips are the most compressible part. */
  .le-tasks { margin-top: 5px; gap: 4px; }
  .le-tasks span { font-size: 9px; padding: 1px 5px; }
  .le-phone { width: auto; max-width: calc(100vw - 20px); max-height: 76vh; }
  .le-pause-card { width: auto; max-width: calc(100vw - 32px); }
  .le-celebrate { width: 88vw; }
}

/*
 * Landscape on a phone: about 390px of height for everything. The status
 * block moves back to the top because there is no room for it above the
 * thumb cluster, and the objective card narrows rather than spanning the
 * width, since in landscape the middle of the screen is the game.
 */
@media (max-height: 460px) and (orientation: landscape) {
  .le-objective {
    top: calc(env(safe-area-inset-top, 0px) + 8px);
    left: calc(env(safe-area-inset-left, 0px) + 12px);
    /* 172, not 216: the money card measures about 344pt with the clock set
       not to wrap, so centred it spans 250..594 of an 844pt screen. A wider
       objective card runs straight into it. */
    right: auto; width: 172px; padding: 7px 10px;
  }
  .le-objective .goal { font-size: 12px; }
  .le-objective .hint { font-size: 10.5px; margin-top: 3px; }
  .le-objective .track, .le-objective .track-label { display: none; }
  body.le-touch .le-objective {
    top: calc(env(safe-area-inset-top, 0px) + 8px);
    left: calc(env(safe-area-inset-left, 0px) + 68px);
  }
  .le-status { top: 8px; bottom: auto; }
  .le-money { padding: 5px 12px 6px; }
  .le-money .amount { font-size: 19px; }
  .le-money .clock { white-space: nowrap; font-size: 10px; }
  .le-vitals {
    top: calc(env(safe-area-inset-top, 0px) + 8px);
    right: calc(env(safe-area-inset-right, 0px) + 12px);
    bottom: auto; flex-direction: row;
  }
  .le-prompt { bottom: 112px; }
  .le-work { bottom: 100px; left: 196px; right: 196px; transform: none; width: auto; }
  /* Landscape has the least vertical room of any layout, and the hint line
     already names the task in hand, so the chips are redundant here. */
  .le-tasks { display: none; }
  .le-toasts { top: 74px; right: calc(env(safe-area-inset-right, 0px) + 12px); width: 240px; left: auto; }
  .le-phone { max-height: calc(100vh - 16px); }
  .le-modal { align-items: center; padding-top: 0; }
  .le-touch-stick { width: 120px; height: 120px; }
  .le-touch-stick i { width: 52px; height: 52px; }
  .le-touch-btn { width: 56px; height: 56px; }
  .le-touch-interact { width: 66px; height: 66px; }
}

`;

export function injectStyles(): void {
  if (document.getElementById('le-styles')) return;
  const style = document.createElement('style');
  style.id = 'le-styles';
  style.textContent = UI_CSS;
  document.head.appendChild(style);
}
